// acceptanceUploadSet — RN2/#70 item 4: the set of files a "send signed document" email actually
// attaches — EVERY non-deleted document of the current acceptance version (all signed PDFs and all
// evidences), signed first. Mirrors Sun's `SignedDocumentLookup.allActive` so the preview and the
// size warning show exactly what travels, in the same order.
//
// Close to `AcceptanceDocumentsList` (which also flattens the current version's non-deleted
// documents) but with one extra rule: if the version has no surviving signed document, nothing is
// attachable at all and this returns `[]` (an evidence never travels alone) — the browse list still
// shows surviving evidences. The two rules must not be merged.
import { apiGet } from '../generic/Api_Functions'

const SIGNED = 'SIGNED'

// The signed PDF heads the batch; evidences follow in a stable filename order — same comparator as
// Sun's, so the preview order matches the attachment order of the email.
const signedFirstThenFilename = (left, right) => {
  const byKind = (left.kind === SIGNED ? 0 : 1) - (right.kind === SIGNED ? 0 : 1)
  if (byKind !== 0) return byKind
  return String(left.originalFilename).localeCompare(String(right.originalFilename))
}

/**
 * #70 (item 4) — the whole current-version set a "send signed" email attaches: EVERY non-deleted
 * document of the current acceptance version (all signed PDFs and all evidences), signed first, in
 * the same order Sun sends them (`SignedDocumentLookup.allActive`). Sun scopes the
 * `signed-document/versions` payload to the current version, so this flattens all of its groups
 * (each signed PDF is uploaded as its own file-version).
 *
 * Returns `[]` when the version has no surviving signed document: an evidence never travels without
 * a signed document (mirrors Sun's `hasActiveSigned` reject).
 */
export const activeUploadSetOf = (versions) => {
  const all = (Array.isArray(versions) ? versions : [])
    .flatMap((group) => (Array.isArray(group.documents) ? group.documents : []))
    .filter((document) => !document.deletedAt)
  if (!all.some((document) => document.kind === SIGNED)) return []
  return all.sort(signedFirstThenFilename)
}

/** Total size of the batch — feeds the D10/S16 "attachment too large" warning before sending. */
export const totalSizeBytesOf = (documents) =>
  documents.reduce((total, document) => total + (Number(document.sizeBytes) || 0), 0)

export const fetchActiveUploadSet = (fabricationOrderId) =>
  apiGet(`fabricationOrderAcceptance/${fabricationOrderId}/signed-document/versions`)
    .then(activeUploadSetOf)
    .catch(() => [])
